import {
	UserPaginated,
	UserSearchCriteria,
} from '@matech/thebigpos-sdk'
import {
	InfiniteData,
	useInfiniteQuery,
	UseInfiniteQueryResult,
} from '@tanstack/react-query'

import { PaginationParams } from '../types'
import queryKeys from '../services/queryKeys'
import { TheBigPOSApi } from '../lib/TheBigPOSClient'

type UsePaginatedUsersBaseProps = {
	params: UserSearchCriteria
	paginationParams: Omit<PaginationParams, 'pageNumber'>
	refetchOnMount?: boolean
	initialPageParam?: number
	staleTime?: number
}

type UsePaginatedUsersProps<TSelected> =
	UsePaginatedUsersBaseProps & {
		select?: (data: InfiniteData<UserPaginated, number>) => TSelected
	}

const usePaginatedUsers = <
	TSelected = InfiniteData<UserPaginated, number>,
>({
	params,
	paginationParams,
	refetchOnMount,
	initialPageParam = 1,
	staleTime = 0,
	select,
}: UsePaginatedUsersProps<TSelected>): UseInfiniteQueryResult<
	TSelected,
	unknown
> => {
	return useInfiniteQuery({
		queryKey: [queryKeys.users, params.searchText ?? ''],
		queryFn: async ({ pageParam: pageNumber }) => {
			const response = await TheBigPOSApi.searchUsers(params, {
				pageNumber,
				pageSize: paginationParams.pageSize,
				sortBy: paginationParams.sortBy,
				sortDirection: paginationParams.sortDirection,
			})

			return response.data
		},
		refetchOnMount,
		initialPageParam,
		getNextPageParam: (lastPage, allPages) => {
			if (!paginationParams.pageSize) return undefined

			if (lastPage.rows.length < paginationParams?.pageSize) {
				return undefined
			}

			return allPages.length + 1
		},
		staleTime,
		select,
	})
}

export default usePaginatedUsers
